import React, {useEffect, useState} from "react";
import {Button, Checkbox, Col, DatePicker, Form, Input, InputNumber, Radio, Row, Select, Space} from "antd";
import {EditOutlined, MinusCircleOutlined, PlusOutlined,} from "@ant-design/icons";
import moment from "moment";
import { v4 as uuid } from "uuid";
import styled from "@emotion/styled";
import {TopBar} from "screens/employer/create-employer/top-bar";
import {changeArrayToStringInObject, useDocumentTitle} from "utils";
import {Profiler} from "components/profiler";
import {District} from "components/district";
import {HelperPaymentMethodSelect} from "components/helper-payment-method";
import {useAddHelper} from "../../../utils/helper";
import {useNavigate} from "react-router-dom";
import {useHelperPositions} from "../../../utils/helper-position";
import PageContainer from "../../../components/layout/PageContainer";

export interface BankAccount {
  [key: string]: number | string | boolean;
  bankName: string;
  default: boolean;
  paymentMethod: number;
  accountName: string;
  bankNumber: string;
}

export const CreateHelperScreen = () => {
  const navigate = useNavigate();

  useDocumentTitle("create helpers", false);
  const [form] = Form.useForm();

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const { data: helperPositions } = useHelperPositions();

  const { mutate, isLoading, isError, isSuccess, data } = useAddHelper();

  useEffect(() => {
    if (isSuccess && data) {
      // @ts-ignore
      navigate(`/helper/${data?.uuid}`);
    }
  }, [isSuccess]);

  const onSubmit = async (values?: any) => {

    if (values.bankDetails.length === 0) {
      return alert("please enter your bank account");
    }



    if (values.dob) {
      values.dob = moment(values.dob).format("YYYY-MM-DD");
    }

    if (values.dateOfVaccines) {
      values.dateOfVaccines = moment(values.dateOfVaccines).format("YYYY-MM-DD");
    }

    if (values.timeTable && values.timeTable.length > 0) {
      values.timeTable = values.timeTable.reduce(
          (a: any, v: string) => ({...a, [v]: true}),
          {}
      );
    }

    await mutate(changeArrayToStringInObject(values));
  };

  return (
    <Profiler id="create helpers">
      <PageContainer>
        <Form
          form={form}
          layout="vertical"
          onFinish={onSubmit}

          onValuesChange={(changedValues, allValues) => {

            if (changedValues.bankDetails) {
              let itemIndex = 0;
              let isUpdateDefault = false;

              for (let i = 0; i < changedValues.bankDetails.length; i++) {
                if (changedValues.bankDetails[i] && changedValues.bankDetails[i].default !== undefined) {
                  itemIndex = i;
                  isUpdateDefault = true;
                  break;
                }
              }

              if (isUpdateDefault) {
                let newBankDetails = [];
                let bankDetails = allValues?.bankDetails || [];

                for (let i = 0; i < bankDetails.length; i++) {
                  if (i === itemIndex) {
                    newBankDetails.push(bankDetails[i]);
                  } else {
                    newBankDetails.push({
                      ...bankDetails[i],
                      default: false,
                    });
                  }
                }

                form.setFieldsValue({
                  ...allValues,
                  bankDetails: newBankDetails
                });
              }
            }
          }}
        >
          <TopBar title="create helpers" />
          <ContentContainer>
            <FormCard>
              <CardTitle>help information</CardTitle>
              <Row gutter={[16, 4]}>
                <Col span={8}>
                  <Form.Item
                    name="nameCn"
                    label="name"
                    rules={[{ required: true, message: "please enter your name" }]}
                  >
                    <Input placeholder="name" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="nameEn"
                    label="name in english"
                    rules={[{ required: true, message: "please enter your name in english" }]}
                  >
                    <Input placeholder="name in english" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 4]}>
                <Col span={5}>
                  <Form.Item
                    name="gender"
                    label="gender"
                    rules={[{ required: true, message: "please enter your gender" }]}
                  >
                    <Radio.Group>
                      <Radio value={1}>females</Radio>
                      <Radio value={0}>male</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item
                    name="idCardNo"
                    label="id number"
                    rules={[{ required: true, message: "please enter your id number" }]}
                  >
                    <Input placeholder="id number" />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item
                    name="idCardName"
                    label="name on the id card"
                    rules={[{ required: true, message: "please enter you name in id card" }]}
                  >
                    <Input placeholder="name on the id card" />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item
                    name="dob"
                    label="date of birth"
                    rules={[{ required: true, message: "please enter your date of birth" }]}
                  >
                    <DatePicker
                      format={"DD/MM/YYYY"}
                      placeholder="DD/MM/YYYY"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 4]}>
                <Col span={5}>
                  <Form.Item
                    name="height"
                    label="height (cm)"
                    rules={[{ required: true, message: "please enter your height" }]}
                  >
                    <InputNumber
                      min={0}
                      max={250}
                      style={{ width: 180 }}
                      placeholder="height (cm)"
                    />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item
                    name="weight"
                    label="weight (kg)"
                    rules={[{ required: true, message: "please enter your weight" }]}
                  >
                    <InputNumber
                      min={0}
                      max={300}
                      style={{ width: 180 }}
                      placeholder="weight (kg)"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 4]}>
                <Col span={6}>
                  <Form.Item
                    name="position"
                    label="rank"
                    rules={[{ required: true, message: "please enter helper rank" }]}
                  >
                    <Select placeholder={"rank"} style={{ width: "100%" }}>
                      {(helperPositions?.list || []).map((item) => (
                        <Select.Option key={item.uuid} value={item.name}>
                          {item.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="language"
                    label="language"
                    rules={[{ required: true, message: "please enter a language" }]}
                  >
                    <Select mode="multiple" placeholder="language">
                      <Select.Option value="cantonese">cantonese</Select.Option>
                      <Select.Option value="english">english</Select.Option>
                      <Select.Option value="putonghua">mandarin</Select.Option>
                      {/*<Select.Option value="潮洲話">潮洲話</Select.Option>*/}
                      {/*<Select.Option value="上海話">上海話</Select.Option>*/}
                      {/*<Select.Option value="福建話">福建話</Select.Option>*/}
                      {/*<Select.Option value="客家話">客家話</Select.Option>*/}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 4]}>
                <Col span={6}>
                  <Form.Item
                    name="phone"
                    label="電話"
                    rules={[{ required: true, message: "please enter a phone number" }]}
                  >
                    <Input placeholder="phone" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="email" label="email">
                    <Input placeholder="email" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="emergencyContactPerson"
                    label="emergency contacts"
                    rules={[{ required: true, message: "please enter an emergency contact" }]}
                  >
                    <Input placeholder="emergency contacts" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name="emergencyContactPhone"
                    label="emergency contact phone number"
                    rules={[
                      { required: true, message: "please enter an emergency contact number" },
                    ]}
                  >
                    <Input placeholder="emergency contact phone number" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 4]}>
                <Col span={6}>
                  <Form.Item
                    name="district"
                    label="region"
                    rules={[{ required: true, message: "please enter a region" }]}
                  >
                    <District />
                  </Form.Item>
                </Col>
                <Col span={18}>
                  <Form.Item
                    name="address"
                    label="full address"
                    rules={[{ required: true, message: "please enter your full address" }]}
                  >
                    <Input placeholder="full address" />
                  </Form.Item>
                </Col>
              </Row>
              {/* <Row>
                <Col span={6}>
                  <Upload
                    action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
                    listType="picture"
                  >
                    <Button icon={<UploadOutlined />}>上傳幫手身分證</Button>
                  </Upload>
                </Col>
              </Row> */}
            </FormCard>

            <FormCard>
              <CardTitle>vaccination</CardTitle>
              <Row gutter={[16, 4]}>
                {/* <Col span={6}>
                  <Form.Item label="接種新冠肺炎疫苗">
                    <Radio.Group>
                      <Radio value="y">有</Radio>
                      <Radio value="n">沒有</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col> */}
                <Col span={6}>
                  <Form.Item
                      name="typesOfVaccines"
                      label="types of vaccines / frequency"
                      rules={[{required: true, message: "please enter the type of vaccine / frequency"}]}
                  >
                    <Input placeholder="types of vaccines"/>
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                      name="dateOfVaccines"
                      label="date of vaccination"
                      // rules={[{ required: true, message: "請輸入接種日期" }]}
                  >
                    <DatePicker placeholder="date of vaccination"/>
                  </Form.Item>
                </Col>
              </Row>
            </FormCard>

            <FormCard>
              <CardTitle>banks</CardTitle>
              <Form.List name="bankDetails">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline">
                      <Form.Item
                        {...field}
                        key={uuid()}
                        label="the name of the bank"
                        name={[field.name, "bankName"]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        {...field}
                        key={uuid()}
                        label="account holder's name"
                        name={[field.name, "accountName"]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        {...field}
                        key={uuid()}
                        label="account number"
                        name={[field.name, "bankNumber"]}
                      >
                        <Input />
                      </Form.Item>

                      <Form.Item
                        {...field}
                        key={uuid()}
                        label="grain delivery method"
                        style={{ width: 140 }}
                        name={[field.name, "paymentMethod"]}
                      >
                        <HelperPaymentMethodSelect />
                      </Form.Item>

                      <Form.Item
                        {...field}
                        key={uuid()}
                        label=" "
                        valuePropName="checked"
                        name={[field.name, "default"]}
                      >
                        <Checkbox>Default</Checkbox>
                      </Form.Item>

                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </Space>
                  ))}

                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      banks account
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </FormCard>

            <FormCard>
              <CardTitle>skills</CardTitle>
              <Row gutter={[16, 4]}>
                <Col span={24}>
                  <Form.Item
                    name="dailyCareSkills"
                    label="daily care skills"
                    // rules={[{ required: true, message: "請選擇日常護理技能" }]}
                  >
                    <Checkbox.Group style={{ width: "100%" }}>
                      <Row>
                        <Col span={4}>
                          <Checkbox value="simple cooking">simple cooking</Checkbox>
                        </Col>
                        <Col span={4}>
                          <Checkbox value="diaper changes">diaper changes</Checkbox>
                        </Col>
                        <Col span={4}>
                          <Checkbox value="hug">hug</Checkbox>
                        </Col>
                        <Col span={4}>
                          <Checkbox value="feeding">feeding</Checkbox>
                        </Col>
                        <Col span={4}>
                          <Checkbox value="reminder to take medication">reminder to take medication</Checkbox>
                        </Col>
                        <Col span={4}>
                          <Checkbox value="bathes">bathes</Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="professionalNursingSkills"
                    label="professional nursing skills"
                    rules={[{ required: true, message: "please select a professional nursing skill" }]}
                  >
                    <Checkbox.Group style={{ width: "100%" }}>
                      <Row>
                        <Col span={6}>
                          <Checkbox value="stomach and throat feeding">stomach and throat feeding</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="post operative care">post operative care</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="simple wound management">simple wound management</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="measure the signs of life">measure the signs of life</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="check blood sugar, urine">
                            check blood sugar, urine
                          </Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="dispose of urine bags">dispose of urine bags</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="PEG">PEG</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="ventilators">ventilators</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="peritoneal dialysis">peritoneal dialysis</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="spiritual services/pain management">
                            spiritual services/pain management
                          </Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="hospitalization, admissions and transfer skills">
                            hospitalization, admissions and transfer skills
                          </Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="rehabilitation exercises">rehabilitation exercises</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="massage with certificate">
                            massage with certificate）
                          </Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="clean bed and change linen">
                            clean bed and change linen
                          </Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="wound care and replacement of hemostatic pads">
                            wound care and replacement of hemostatic pads
                          </Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="stoma care（including：abdominal stoma/colostomy care)">
                            stoma care（including：abdominal stoma/colostomy care)
                          </Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="injections and administration of medications">
                            injections and administration of medications
                          </Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="basic first aid（there are certificates）">
                            basic first aid（there are certificates）
                          </Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="advancedNursingSkills"
                    label="advanced nursing skills"
                    // rules={[{ required: true, message: "請選擇進階護理技能" }]}
                  >
                    <Checkbox.Group style={{ width: "100%" }}>
                      <Row>
                        <Col span={6}>
                          <Checkbox value="cardiopulmonary revival（certificates）">
                            cardiopulmonary revival（certificates）
                          </Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="vaccinations and immunizations">
                            vaccinations and immunizations
                          </Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="Techniques to assist patients in preparing for X-rays and receiving ECG tests">
                            Techniques to assist patients in preparing for X-rays and receiving ECG tests
                          </Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="phlegm">phlegm</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="blood drawn">blood drawn</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="replace the stomach and throat">replace the stomach and throat</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="replace the urinary throat">replace the urinary throat</Checkbox>
                        </Col>
                        <Col span={6}>
                          <Checkbox value="insulin shots">insulin shots</Checkbox>
                        </Col>
                      </Row>
                    </Checkbox.Group>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item
                    name="otherSkill"
                    label="other"
                    style={{width: 280}}
                    // rules={[{ required: true, message: "請選擇日常護理技能" }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </FormCard>

            <FormCard>
              <CardTitle>timetable</CardTitle>
              <InfoLabel>early:00:00-11:59 night:12:00-24:00</InfoLabel>
              <br />
              <Form.Item
                name="timeTable"
                rules={[{ required: true, message: "please select a schedule" }]}
              >
                <Checkbox.Group>
                  <Row gutter={[16, 4]}>
                    <Col flex={1}>
                      <div>Monday</div>
                      <Checkbox value="monAm">early</Checkbox>
                      <br />
                      <br />
                      <Checkbox value="monPm">night</Checkbox>
                    </Col>
                    <Col flex={2}>
                      <div>Tuesday</div>
                      <Checkbox value="tueAm">early</Checkbox>
                      <br />
                      <br />
                      <Checkbox value="tuePm">night</Checkbox>
                    </Col>
                    <Col flex={3}>
                      <div>Wednesday</div>
                      <Checkbox value="wedAm">early</Checkbox>
                      <br />
                      <br />
                      <Checkbox value="wedPm">night</Checkbox>
                    </Col>
                    <Col flex={4}>
                      <div>Thursday</div>
                      <Checkbox value="thuAm">early</Checkbox>
                      <br />
                      <br />
                      <Checkbox value="thuPm">night</Checkbox>
                    </Col>
                    <Col flex={5}>
                      <div>Friday</div>
                      <Checkbox value="friAm">early</Checkbox>
                      <br />
                      <br />
                      <Checkbox value="friPm">night</Checkbox>
                    </Col>
                    <Col flex={6}>
                      <div>Saturday</div>
                      <Checkbox value="satAm">early</Checkbox>
                      <br />
                      <br />
                      <Checkbox value="satPm">night</Checkbox>
                    </Col>
                    <Col flex={7}>
                      <div>Sunday</div>
                      <Checkbox value="sunAm">early</Checkbox>
                      <br />
                      <br />
                      <Checkbox value="sunPm">night</Checkbox>
                    </Col>
                  </Row>
                </Checkbox.Group>
              </Form.Item>
            </FormCard>
          </ContentContainer>
        </Form>
      </PageContainer>
    </Profiler>
  );
};

const ContentContainer = styled.div`
  padding: 3.2rem;
`;

const FormCard = styled.div`
  padding: 2.4rem;
  background-color: #ffffff;
  margin-bottom: 2.4rem;
`;

const CardTitle = styled.div`
  color: #3c3e42;
  font-size: 20px;
  font-weight: bold;
  padding-bottom: 10px;
`;

const InfoLabel = styled.div`
  color: #8d939e;
  font-size: 12px;
`;

CreateHelperScreen.whyDidYouRender = false;
